import * as _ from "lodash";
import qs from "qs";
import { isFeatureEnabled } from "src/utilities";

import { CustomerGroupBody } from "../lib/models/CustomerGroup";
import HostService from "../services/Host";
import {
  CustomerContactsInterval,
  CustomerContactsState,
  CustomerContactsTopFlopCombinationsState,
  CustomerGroupFilterState,
  CustomerSectorsStatistics,
  DashboardFilterState,
  FeatureState,
  MarketShareFilterState,
  ProductType,
  Tariff,
  TariffTypesPayload,
} from "../types/index";
import IUserRequestType from "../types/UserRequestType";
import IUserResponseType from "../types/UserResponseType";
import { getCustomerGroupFilterForQuery, handleResponse } from "./utilities";

export async function login(
  username: string,
  password: string
): Promise<string> {
  const response = await fetch(`${HostService.getApiHost()}/auth`, {
    body: JSON.stringify({ username, password }),
    headers: { "Content-Type": "application/json; charset=utf-8" },
    method: "POST",
  });
  if (!response.ok) {
    if (response.status === 401) {
      const { error } = await response.json();
      return Promise.reject(error);
    }
    return Promise.reject();
  }
  const body = await response.json();
  return body.token;
}

function joinFilter(filter: { [field: string]: string }): string {
  return _.map(filter, (value, key) => {
    return `${key}=${value}`;
  }).join("&");
}

export async function fetchUserData(
  token: string
): Promise<IUserResponseType[]> {
  const url = `${HostService.getApiHost()}/users`;
  const response = await fetch(url, {
    headers: { token },
  });

  const body = await handleResponse(response);
  return body.data;
}

export async function updateUser(
  token: string,
  user: IUserRequestType
): Promise<IUserResponseType> {
  const url = `${HostService.getApiHost()}/users/${user.id}`;
  const response = await fetch(url, {
    body: JSON.stringify(user),
    headers: {
      "Content-Type": "application/json",
      token,
    },
    method: "PUT",
  });

  const body = await handleResponse(response);
  return body;
}

export async function createUser(
  token: string,
  user: IUserRequestType
): Promise<IUserResponseType> {
  const url = `${HostService.getApiHost()}/users`;
  const response = await fetch(url, {
    body: JSON.stringify(user),
    headers: {
      "Content-Type": "application/json",
      token,
    },
    method: "POST",
  });

  const body = await handleResponse(response);
  return body;
}

export async function deleteUser(
  token: string,
  user: IUserRequestType
): Promise<void> {
  const url = `${HostService.getApiHost()}/users/${user.id}`;
  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      token,
    },
    method: "DELETE",
  });

  await handleResponse(response);
}

export async function fetchCluster(
  token: string,
  dashboardFilter: DashboardFilterState
): Promise<Array<{ date: string; min: number; mid: number; max: number }>> {
  const filter = {
    customer_type: dashboardFilter.customerType,
    date_range: dashboardFilter.dateRange,
    electricity_filter: dashboardFilter.electricityFilter,
    gas_filter: dashboardFilter.gasFilter,
    heating_filter: dashboardFilter.heatingFilter,
    glass_fiber_filter: dashboardFilter.glassFiberFilter,
    score: dashboardFilter.score,
    filterGroupsEnabled: dashboardFilter.filterGroupsEnabled,
  };
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/cluster?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  const body = await handleResponse(response);
  return body.data;
}

export async function fetchCount(
  token: string,
  dashboardFilter: DashboardFilterState
): Promise<{
  customerCount: number;
  churnCount: number;
  churnCountBeginTimestamp: string | null;
  churnCountEndTimestamp: string | null;
  // contractCount: number;
  contractElectricityCount: number;
  contractGasCount: number;
  contractHeatingCount: number;
  contractGlassFiberCount: number;
}> {
  const filter = {
    customer_type: dashboardFilter.customerType,
    date_range: dashboardFilter.dateRange,
    electricity_filter: dashboardFilter.electricityFilter,
    gas_filter: dashboardFilter.gasFilter,
    heating_filter: dashboardFilter.heatingFilter,
    glass_fiber_filter: dashboardFilter.glassFiberFilter,
    score: dashboardFilter.score,
    filterGroupsEnabled: dashboardFilter.filterGroupsEnabled,
  };
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/count?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });
  const body = await handleResponse(response);
  return body.data;
}

export async function fetchNbaDistribution(
  token: string,
  dashboardFilter: DashboardFilterState
): Promise<{
  nbaTopTen: Array<{
    label: string;
    value: string;
  }>;
}> {
  const filter = {
    customer_type: dashboardFilter.customerType,
    date_range: dashboardFilter.dateRange,
    electricity_filter: dashboardFilter.electricityFilter,
    gas_filter: dashboardFilter.gasFilter,
    glass_fiber_filter: dashboardFilter.glassFiberFilter,
    heating_filter: dashboardFilter.heatingFilter,
    filterGroupsEnabled: dashboardFilter.filterGroupsEnabled,
  };
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/nba-distribution?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  const body = await handleResponse(response);
  return body.data;
}

export async function fetchPostalcodes(
  token: string,
  dashboardFilter: DashboardFilterState
): Promise<any> {
  const filter = {
    electricity_filter: dashboardFilter.electricityFilter,
    gas_filter: dashboardFilter.gasFilter,
    heating_filter: dashboardFilter.heatingFilter,
    glass_fiber_filter: dashboardFilter.glassFiberFilter,
    customer_type: dashboardFilter.customerType,
    date_range: dashboardFilter.dateRange,
    score: dashboardFilter.score,
    filterGroupsEnabled: dashboardFilter.filterGroupsEnabled,
  };
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/postalcodes?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  const body = await handleResponse(response);
  return body;
}

export async function fetchTariffTypes(
  token: string,
  productType: ProductType
): Promise<{
  data: TariffTypesPayload;
}> {
  const url = `${HostService.getApiHost()}/tariff-type/${productType}`;
  const response = await fetch(url, {
    headers: { token },
  });

  const body = await handleResponse(response);
  return body;
}

export async function fetchHousingSituations(
  token: string
): Promise<{
  data: {
    buildingTypes: Array<{ id: number; name: string }>;
    buildingAges: Array<{ id: number; name: string }>;
  };
}> {
  const url = `${HostService.getApiHost()}/housing-situations`;
  const response = await fetch(url, {
    headers: { token },
  });

  const body = await handleResponse(response);
  return body;
}

export async function fetchLineChartsData(
  token: string,
  dashboardFilter: DashboardFilterState
): Promise<{ data: Array<{ date: string; avg: string; sum: string }> }> {
  const filter = {
    customer_type: dashboardFilter.customerType,
    date_range: dashboardFilter.dateRange,
    electricity_filter: dashboardFilter.electricityFilter,
    gas_filter: dashboardFilter.gasFilter,
    heating_filter: dashboardFilter.heatingFilter,
    glass_fiber_filter: dashboardFilter.glassFiberFilter,
    score: dashboardFilter.score,
    filterGroupsEnabled: dashboardFilter.filterGroupsEnabled,
  };
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/line?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  return handleResponse(response);
}

export async function fetchCustomerAddressPostalCodes(
  token: string
): Promise<{ data: string[] }> {
  const url = `${HostService.getApiHost()}/customer-address-postal-code`;
  const response = await fetch(url, {
    headers: { token },
  });

  const body = await handleResponse(response);
  return body;
}

export async function fetchEffects(
  token: string,
  columns: string[],
  customerType: string
): Promise<{
  data: Array<{
    column_name: string;
    effect: string;
    affected_rows: number;
    column_value: string;
    target_name: string;
    customer_type: string;
  }>;
}> {
  const url = `${HostService.getApiHost()}/effects?columns=${columns.join(
    ","
  )}&customer-type=${customerType}`;
  const response = await fetch(url, {
    headers: { token },
  });

  return handleResponse(response);
}

export async function fetchCustomerGroup(
  token: string,
  customerGroupFilter: CustomerGroupFilterState,
  page?: number,
  rowsPerPage?: number,
  filter?: { [field: string]: string },
  sortBy?: string,
  sortDirection?: string
): Promise<CustomerGroupBody> {
  let url = getCustomerGroupUrl(
    customerGroupFilter,
    false,
    filter,
    sortBy,
    sortDirection,
    page,
    rowsPerPage
  );
  const response = await fetch(url, {
    headers: { token },
  });

  return handleResponse(response);
}

export async function fetchCustomerDensity(
  token: string,
  customerGroupFilter: CustomerGroupFilterState
): Promise<any> {
  const filter = getCustomerGroupFilterForQuery(customerGroupFilter, {
    omit: ["polygonMapSelection"],
  });
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/customer-density?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  return handleResponse(response);
}

export async function fetchMarketShare(
  token: string,
  marketShareFilter: MarketShareFilterState
): Promise<any> {
  const filter = {
    area_type: marketShareFilter.areaType,
    customer_address_postal_codes: marketShareFilter.selectedPostalCodes.map(
      (code) => code.value
    ),
  };
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/market-share?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  return handleResponse(response);
}

export async function fetchPolygonDensity(
  token: string,
  customerGroupFilter: CustomerGroupFilterState
): Promise<any> {
  const filter = getCustomerGroupFilterForQuery(customerGroupFilter, {});
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/polygon-density?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  return handleResponse(response);
}

const getFeaturesForTableString = () => {
  const features = {
    housing_situation_feature: isFeatureEnabled(
      process.env.REACT_APP_FEATURE_HOUSING_SITUATION
    )
      ? FeatureState.INCLUDE
      : FeatureState.EXCLUDE,
    glass_fiber_feature: isFeatureEnabled(
      process.env.REACT_APP_FEATURE_GLASS_FIBER
    )
      ? FeatureState.INCLUDE
      : FeatureState.EXCLUDE,
  };
  return Object.entries(features)
    .map(([feature, value]) => `${feature}=${value}`)
    .join("&");
};

export function getCustomerGroupUrl(
  customerGroupFilter: CustomerGroupFilterState,
  download?: boolean,
  tableFilter?: { [field: string]: string },
  sortBy?: string,
  sortDirection?: string,
  page?: number,
  rowsPerPage?: number
): string {
  const filter = getCustomerGroupFilterForQuery(customerGroupFilter, {});
  let queryString = qs.stringify(filter);

  if (page) {
    queryString = `${queryString}&page=${page}`;
  }

  if (rowsPerPage) {
    queryString = `${queryString}&rows-per-page=${rowsPerPage}`;
  }

  if (sortBy && sortDirection) {
    queryString = `${queryString}&order-by=${sortBy}&order=${sortDirection}`;
  }

  queryString = tableFilter
    ? `${queryString}&${joinFilter(tableFilter)}`
    : queryString;

  if (download) {
    return `${HostService.getApiHost()}/customer-group-download?${queryString}&${getFeaturesForTableString()}`;
  }

  return `${HostService.getApiHost()}/customer-group?${queryString}&${getFeaturesForTableString()}`;
}

export async function fetchLatLong(
  token: string,
  customerGroupFilter: CustomerGroupFilterState
): Promise<any> {
  const filter = getCustomerGroupFilterForQuery(customerGroupFilter, {});
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/lat-long?${queryString}`;
  const response = await fetch(url, {
    headers: { token },
  });

  return handleResponse(response);
}

export function getDocumentationUrl(): string {
  return `${HostService.getApiHost()}/documentation`;
}

export async function fetchEffectsColumns(
  token: string,
  customerType: string
): Promise<{
  data: Array<{
    columnName: string;
    columns: string[];
  }>;
}> {
  const url = `${HostService.getApiHost()}/effects-columns?&customer-type=${customerType}`;
  const response = await fetch(url, {
    headers: { token },
  });
  return handleResponse(response);
}

export async function fetchSalutations(
  token: string
): Promise<{
  data: Array<{
    salutation: string;
  }>;
}> {
  const url = `${HostService.getApiHost()}/salutations`;
  const response = await fetch(url, {
    headers: { token },
  });
  return handleResponse(response);
}

export async function fetchNextBestActivities(
  token: string
): Promise<{
  data: { nextBestActivities: Array<{ name: string; uuid: string }> };
}> {
  const url = `${HostService.getApiHost()}/next-best-activities`;
  const response = await fetch(url, {
    headers: { token },
  });
  return handleResponse(response);
}

export async function fetchCustomerContacts({
  token,
  interval,
}: {
  token: string;
  interval: CustomerContactsInterval;
}): Promise<{
  data: Partial<CustomerContactsState>;
}> {
  const url = `${HostService.getApiHost()}/customer-contacts?interval=${interval}`;
  const response = await fetch(url, {
    headers: {
      token,
    },
    method: "GET",
  });
  return handleResponse(response);
}

export async function fetchCustomerContactsTopFlopCombinations({
  token,
  nba_uuid,
  categories,
}: {
  token: string;
  nba_uuid: string;
  categories: string[];
}): Promise<{
  data: Partial<CustomerContactsTopFlopCombinationsState>;
}> {
  const url = `${HostService.getApiHost()}/customer-contacts/top-flop-combinations/?categories=${categories}&nba_uuid=${nba_uuid}`;
  const response = await fetch(url, {
    headers: {
      token,
    },
    method: "GET",
  });
  return handleResponse(response);
}

export async function fetchCustomerSectorsStatistics(
  token: string,
  dashboardFilter: DashboardFilterState
): Promise<{
  data: Omit<CustomerSectorsStatistics, "loading" | "pending">;
}> {
  const filter = {
    customer_type: dashboardFilter.customerType,
    date_range: dashboardFilter.dateRange,
    electricity_filter: dashboardFilter.electricityFilter,
    gas_filter: dashboardFilter.gasFilter,
    heating_filter: dashboardFilter.heatingFilter,
    glass_fiber_filter: dashboardFilter.glassFiberFilter,
    filterGroupsEnabled: dashboardFilter.filterGroupsEnabled,
  };
  const queryString = qs.stringify(filter);
  const url = `${HostService.getApiHost()}/customer-sectors-statistics?${queryString}`;

  const response = await fetch(url, {
    headers: { token },
  });
  return handleResponse(response);
}

export * from "./customer-groups";



// WEBPACK FOOTER //
// ./src/api/index.tsx